import React, { useState, useEffect, useCallback } from 'react';
import api, { API_URL } from '../../services/api';
import { Coins, ListOrdered, Users, RefreshCw } from 'lucide-react';

// ACHATS OR / STOCK MÉTAL couche 1 (Lot 3, 25/06/2026) — visualisation des achats d'or.
// Backend /api/achats-metal (gated couts_required). Donnée financière/appro.

const toast = (type, msg) => {
  if (window.toast && window.toast[type]) window.toast[type](msg);
  else if (type === 'error') alert(msg);
};
const fmtG = (v) => (v == null || isNaN(v)) ? '—' : parseFloat(v).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtDH = (v) => (v == null || isNaN(v)) ? '—' : parseFloat(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function AchatsOr() {
  const [vue, setVue] = useState('synthese'); // 'synthese' | 'achats' | 'fournisseurs'
  const [synthese, setSynthese] = useState(null);
  const [achats, setAchats] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fRef, setFRef] = useState('');
  const [fAnnee, setFAnnee] = useState('');

  const loadSynthese = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`${API_URL}/achats-metal/synthese`); setSynthese(r.data); }
    catch (e) { toast('error', 'Erreur chargement synthèse'); }
    finally { setLoading(false); }
  }, []);

  const loadAchats = useCallback(async () => {
    setLoading(true);
    try {
      const qs = [];
      if (fRef) qs.push(`ref=${encodeURIComponent(fRef)}`);
      if (fAnnee) qs.push(`annee=${encodeURIComponent(fAnnee)}`);
      const r = await api.get(`${API_URL}/achats-metal${qs.length ? '?' + qs.join('&') : ''}`);
      setAchats(r.data || []);
    } catch (e) { toast('error', 'Erreur chargement achats'); }
    finally { setLoading(false); }
  }, [fRef, fAnnee]);

  const loadFournisseurs = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`${API_URL}/achats-metal/fournisseurs`); setFournisseurs(r.data || []); }
    catch (e) { toast('error', 'Erreur chargement fournisseurs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (vue === 'synthese') loadSynthese(); }, [vue, loadSynthese]);
  useEffect(() => { if (vue === 'achats') loadAchats(); }, [vue, loadAchats]);
  useEffect(() => { if (vue === 'fournisseurs') loadFournisseurs(); }, [vue, loadFournisseurs]);

  // réfs distinctes (pour le filtre) issues de la synthèse
  const refs = (synthese?.lignes || []).map(l => l.ref_metal);

  return (
    <div className="space-y-4">
      {/* Toggle */}
      <div className="bg-white rounded-lg shadow-md p-1 flex gap-1 max-w-xl">
        {[
          { k: 'synthese', label: 'Synthèse', Icon: Coins },
          { k: 'achats', label: 'Achats', Icon: ListOrdered },
          { k: 'fournisseurs', label: 'Fournisseurs', Icon: Users },
        ].map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setVue(k)}
            className={`flex-1 px-3 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-1.5 text-sm transition-all ${
              vue === k ? 'bg-gradient-to-r from-yellow-600 to-amber-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-center py-8 text-gray-400"><RefreshCw className="h-6 w-6 mx-auto animate-spin" /></div>
      )}

      {/* ===== SYNTHÈSE ===== */}
      {vue === 'synthese' && !loading && synthese && (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="min-w-full text-sm" style={{ minWidth: '760px' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase text-xs">Réf métal</th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Achats</th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Total (g)</th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Or fin (g)</th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Total (DH)</th>
                <th className="px-3 py-2.5 text-right font-medium text-amber-700 uppercase text-xs">PMP (DH/g)</th>
                <th className="px-3 py-2.5 text-center font-medium text-gray-500 uppercase text-xs">Période</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {synthese.lignes.map(l => (
                <tr key={l.ref_metal} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 font-mono font-medium text-gray-800">{l.ref_metal}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{l.nb_achats}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">{fmtG(l.total_qte_g)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-500">{fmtG(l.total_or_fin)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">{fmtDH(l.total_montant)}</td>
                  <td className="px-3 py-2.5 text-right text-lg font-extrabold text-amber-800">{fmtDH(l.pmp)}</td>
                  <td className="px-3 py-2.5 text-center text-xs text-gray-400">{l.premier_achat} → {l.dernier_achat}</td>
                </tr>
              ))}
              {synthese.total && (
                <tr className="bg-gray-50 font-bold border-t-2 border-gray-300">
                  <td className="px-3 py-2.5 text-gray-900">{synthese.total.ref_metal}</td>
                  <td className="px-3 py-2.5 text-right">{synthese.total.nb_achats}</td>
                  <td className="px-3 py-2.5 text-right">{fmtG(synthese.total.total_qte_g)}</td>
                  <td className="px-3 py-2.5 text-right">{fmtG(synthese.total.total_or_fin)}</td>
                  <td className="px-3 py-2.5 text-right">{fmtDH(synthese.total.total_montant)}</td>
                  <td className="px-3 py-2.5 text-right text-amber-800">{fmtDH(synthese.total.pmp)}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="px-3 py-2 text-xs text-gray-400">PMP = prix moyen pondéré = coût moyen d'un gramme acheté pour chaque référence.</p>
        </div>
      )}

      {/* ===== ACHATS ===== */}
      {vue === 'achats' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <select value={fRef} onChange={e => setFRef(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">Toutes les réfs</option>
              {refs.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <input type="number" value={fAnnee} onChange={e => setFAnnee(e.target.value)} placeholder="Année"
              className="px-3 py-2 border border-gray-300 rounded-lg w-28 text-sm" />
            <button onClick={loadAchats} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1.5 text-sm font-medium">
              <RefreshCw className="h-4 w-4" /> Filtrer
            </button>
          </div>
          {!loading && (
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
              <table className="min-w-full text-sm" style={{ minWidth: '900px' }}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase text-xs">Date</th>
                    <th className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase text-xs">Réf</th>
                    <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Qté (g)</th>
                    <th className="px-3 py-2.5 text-center font-medium text-gray-500 uppercase text-xs">Titre</th>
                    <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Prix/g</th>
                    <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Montant</th>
                    <th className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase text-xs">Fournisseur</th>
                    <th className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase text-xs">Facture</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {achats.map(a => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{a.date_mouvement}</td>
                      <td className="px-3 py-2.5 font-mono font-medium text-gray-800">{a.ref_metal}</td>
                      <td className="px-3 py-2.5 text-right text-gray-700">{fmtG(a.qte_g)}</td>
                      <td className="px-3 py-2.5 text-center text-gray-500">{a.titre}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{a.prix_g != null ? fmtDH(a.prix_g) : '—'}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-900">{fmtDH(a.montant)}</td>
                      <td className="px-3 py-2.5 text-gray-600">{a.fournisseur || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-400 text-xs">{a.num_facture || '—'}</td>
                    </tr>
                  ))}
                  {achats.length === 0 && (
                    <tr><td colSpan="8" className="px-3 py-8 text-center text-gray-400">Aucun achat</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ===== FOURNISSEURS ===== */}
      {vue === 'fournisseurs' && !loading && (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="min-w-full text-sm" style={{ minWidth: '560px' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase text-xs">Fournisseur</th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Achats</th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Total (g)</th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase text-xs">Total (DH)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {fournisseurs.map((f, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 font-medium text-gray-800">{f.fournisseur}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{f.nb_achats}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700">{fmtG(f.total_qte_g)}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-gray-900">{fmtDH(f.total_montant)}</td>
                </tr>
              ))}
              {fournisseurs.length === 0 && (
                <tr><td colSpan="4" className="px-3 py-8 text-center text-gray-400">Aucun fournisseur</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AchatsOr;
